import {ProductDetailsSection_ArticleDetail_Lane} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/product_details.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import React from 'react';
import styled, {css} from 'styled-components';
import {Advertisement} from '../../../../features/Advertisement/Advertisement';
import {SizeChartComponentProps} from '../../SizeCharts/SizeCharts';
import {BulletPointSection} from '../ProductDetailsSections/BulletPointSection';

import {useIsFitAnalyticsConsetGiven} from '@aboutyou/storage';
import {TrackBASMonitoring} from '@aboutyou/tracking';
import {SustainabilityInfoContent} from '@aboutyou/ui-components';
import {IngredientsSection} from '../ProductDetailsSections/IngredientsSection';
import {ManufacturerInfo} from '../ProductDetailsSections/ManufacturerInfo';
import {MaterialSection} from '../ProductDetailsSections/MaterialSection';
import {RegularSection} from '../ProductDetailsSections/RegularSection';
import {SectionWrapper} from '../ProductDetailsSections/SectionWrapper';
import {SizeSection} from '../ProductDetailsSections/SizeSection';

export const ProductDetailsSection: React.FC<
  {
    section: ProductDetailsSection_ArticleDetail_Lane;
    articleNumber?: string;
    hideAdvertisementLaneOnBigScreens: boolean;
    shouldRenderLabel?: boolean;
    optimizeSustainabilitySpacing?: boolean;
  } & SizeChartComponentProps
> = ({
  section,
  articleNumber,
  hideAdvertisementLaneOnBigScreens,
  SizeChartLayout,
  SizeSystemLayout,
  shouldRenderLabel = true,
}) => {
  const {type} = section;
  const isConsentBannerAccepted = useIsFitAnalyticsConsetGiven();
  if (!type) {
    return null;
  }
  switch (type.$case) {
    case 'regularLane':
      return (
        <RegularSection
          label={shouldRenderLabel ? section.label : ''}
          bulletPoints={type.regularLane.items}
          additionalText={type.regularLane.additionalText}
        />
      );
    case 'bulletPointLane':
      return (
        <BulletPointSection
          label={shouldRenderLabel ? section.label : ''}
          bulletPoints={type.bulletPointLane.bulletPoints}
          articleNumber={articleNumber}
        />
      );
    case 'sizeLane':
      return (
        <SizeSection
          label={shouldRenderLabel ? section.label : ''}
          bulletPoints={type.sizeLane.bulletPoints}
          displaySizeAdviser={
            type.sizeLane.displaySizeAdviser && isConsentBannerAccepted
          }
          additionalText={type.sizeLane.additionalText}
          sizeCharts={type.sizeLane.sizeCharts}
          SizeChartLayout={SizeChartLayout}
          SizeSystemLayout={SizeSystemLayout}
        />
      );
    case 'materialLane':
      return (
        <MaterialSection
          label={shouldRenderLabel ? section.label : ''}
          bulletPoints={type.materialLane.bulletPoints}
          careIcons={type.materialLane.careIcons}
        />
      );
    case 'ingredientsLane':
      return (
        <IngredientsSection
          label={shouldRenderLabel ? section.label : ''}
          attributes={type.ingredientsLane.attributes}
        />
      );
    case 'sustainabilityInfoLane':
      return (
        <SustainabilityInfoContent
          certificate={type.sustainabilityInfoLane.certificate}
          cta={type.sustainabilityInfoLane.cta}
          cluster={type.sustainabilityInfoLane.cluster}
          hideBorders
        />
      );
    case 'advertisementLane':
      if (!type.advertisementLane.advertisement) {
        if (hideAdvertisementLaneOnBigScreens) {
          return null;
        }

        return (
          <TrackBASMonitoring
            basPromotionSource={TrackingPromotionCreative.BasAdpBanner}
          />
        );
      }
      return (
        <StyledSectionWrapper
          hideOnBigScreens={hideAdvertisementLaneOnBigScreens}
          dataTestId={`productDetailsAdvertisement`}
        >
          <Advertisement
            advertisement={type.advertisementLane.advertisement}
            testId="advertisementProductDetails"
            tracking={{
              promotionSource: TrackingPromotionCreative.BasAdpBanner,
            }}
          />
        </StyledSectionWrapper>
      );

    case 'manufacturerLane': {
      return <ManufacturerInfo manufacturerInfo={type.manufacturerLane} />;
    }

    default:
      return null;
  }
};

const StyledSectionWrapper = styled(SectionWrapper)<{
  hideOnBigScreens: boolean;
}>`
  // in big screens the BAS placement will be rendered in the buying box
  ${({hideOnBigScreens}) =>
    hideOnBigScreens &&
    css`
      @media screen and (min-height: 801px) {
        display: none;
      }
    `}
`;
